import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
    name: 'highlighter',
    standalone: false
})
export class HighlighterPipe implements PipeTransform {

    /**
     * Transforms text by highlighting search terms while preventing XSS attacks.
     *
     * @param value - The text to search within
     * @param args - The search term to highlight
     * @param type - 'full' for word boundary matching, other for partial matching
     * @returns HTML string with highlighted terms (XSS-safe)
     */
    transform(value: any, args: any, type: string): string {
        // Handle null, undefined, or empty values
        if (value === null || value === undefined) {
            return '';
        }

        // Convert to string if not already
        const stringValue = String(value);

        // Return early if no search args or empty string
        if (!args || args === '') {
            return stringValue;
        }

        // First, HTML-escape the input value to prevent XSS
        const escapedValue = this.escapeHtml(stringValue);

        // Escape regex special characters in search args to prevent regex injection
        const escapedArgs = this.escapeRegex(String(args));

        // If the escaped search term is empty after escaping, return the escaped value
        if (!escapedArgs) {
            return escapedValue;
        }

        try {
            if (type === 'full') {
                // Match full words only
                const re = new RegExp('\\b(' + escapedArgs + ')\\b', 'igm');
                return escapedValue.replace(re, '<span class="highlighted-text">$1</span>');
            }
                // Match partial strings
                const re = new RegExp('(' + escapedArgs + ')', 'igm');
                return escapedValue.replace(re, '<span class="highlighted-text">$1</span>');

        } catch (e) {
            // If regex fails for any reason, return the escaped value safely
            console.error('HighlighterPipe: Error creating regex', e);
            return escapedValue;
        }
    }

    /**
     * Escapes HTML special characters to prevent XSS attacks.
     * This ensures that any HTML in the input is displayed as text, not executed.
     */
    private escapeHtml(text: string): string {
        const htmlEscapes: { [key: string]: string } = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            '\'': '&#39;'
        };

        return text.replace(/[&<>"']/g, char => htmlEscapes[char]);
    }

    /**
     * Escapes regex special characters to prevent regex injection attacks.
     * This ensures search terms with special characters are matched literally.
     */
    private escapeRegex(text: string): string {
        // Escape all regex special characters
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
